import { ChoiceTag } from "@/components/TagChoice/ChoiceTag"
import { P3 } from "@/components/Typographies"
import { ClusterStatus } from "@/domain/cluster/constants"
import { useAppQuery } from "@/hooks/useAppQuery"
import {
  type ClusterJsonldReadCollectionCluster,
  getClusters,
  getGetClustersQueryKey,
} from "@/networking/api"
import { Flex, Spinner } from "@chakra-ui/react"
import { useMemo } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import type { AddressLocation } from "../../context/ShareRequestInterbuCreateModalContext"
import {
  NEARBY_SUGGESTIONS_COUNT,
  formatDistanceKm,
  pickNearbySuggestions,
  pickSpecialisedSuggestions,
  specialisedClustersFor,
} from "../../helpers/nearbyClusters"

type Props = {
  location: AddressLocation | null
  // The activity of the InterBU and the property's country (ADR 0017, D1).
  activity?: string | null
  country?: string | null
  excludedClusterId?: string | null
  selectedIds: string[]
  onToggle: (cluster: ClusterJsonldReadCollectionCluster) => void
}

// The agencies dedicated to the activity, whatever the distance (ADR 0017, D2), or else the 5
// nearest active agencies of the property address, without the agencies only typed "physical"
// (ADR 0017, D3). A click adds or removes an agency; nothing is preselected.
export const NearbyClusterSuggestions: React.FC<Props> = ({
  location,
  activity,
  country,
  excludedClusterId,
  selectedIds,
  onToggle,
}) => {
  const intl = useIntl()

  const specialised = specialisedClustersFor(activity, country)
  const hasNoSpecialisedAgency =
    specialised !== undefined && "id[]" in specialised && specialised["id[]"].length === 0

  const { flatData, isFetching } = useAppQuery(
    getClusters,
    getGetClustersQueryKey,
    {
      // The distance is shown whenever the address is geocoded.
      ...(location !== null ? { "near[lat]": location.lat, "near[lng]": location.lng } : {}),
      ...(specialised ?? { notPhysicalOnly: true }),
      // Explicit: admins also see archived clusters.
      "status.code": ClusterStatus.ACTIVE,
      // One more than needed, in case the sender agency is among the nearest.
      itemsPerPage: specialised === undefined ? NEARBY_SUGGESTIONS_COUNT + 1 : 20,
      withMinProps: true,
    },
    { enabled: specialised === undefined ? location !== null : !hasNoSpecialisedAgency }
  )

  const suggestions = useMemo(
    () =>
      specialised === undefined
        ? pickNearbySuggestions(flatData ?? [], excludedClusterId)
        : pickSpecialisedSuggestions(flatData ?? [], excludedClusterId),
    [flatData, excludedClusterId, specialised]
  )

  if (specialised === undefined && location === null)
    return (
      <P3 color="textMuted">
        <FormattedMessage id="userSharedData.interbu.modal.steps.2.nearby.noLocation" />
      </P3>
    )

  if (isFetching && !hasNoSpecialisedAgency) return <Spinner size="sm" />

  if (suggestions.length === 0)
    return (
      <P3 color="textMuted">
        <FormattedMessage
          id={
            specialised === undefined
              ? "userSharedData.interbu.modal.steps.2.nearby.none"
              : "userSharedData.interbu.modal.steps.2.specialised.none"
          }
        />
      </P3>
    )

  return (
    <Flex gap="2" wrap="wrap">
      {suggestions.map(cluster => (
        <ChoiceTag
          key={cluster.id}
          isSelected={selectedIds.includes(cluster.id ?? "")}
          toggleSelection={() => onToggle(cluster)}
          label={
            typeof cluster.distance === "number"
              ? `${cluster.name ?? ""}, ${formatDistanceKm(intl, cluster.distance)}`
              : (cluster.name ?? "")
          }
        />
      ))}
    </Flex>
  )
}
